import { 
  Flame, 
  Zap, 
  Target, 
  Users, 
  GraduationCap, 
  BookOpen,
  CheckCircle2,
  CreditCard,
  BarChart3,
} from "lucide-react";

/**
 * Role-specific stats bar that appears below the header in all portals
 * Shows relevant metrics for each user type
 */

// Student Stats Bar - Shows XP, Streak, Accuracy, Questions
export function StudentStatsBar({ user }) {
  const currentStreak = user?.current_streak || 0;
  const totalXP = user?.xp_points || 0;
  const accuracy = user?.accuracy || 0;
  const totalQuestions = user?.total_questions || 0;

  return (
    <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white py-2 px-4 shadow-md" data-testid="student-stats-bar">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-around sm:justify-center sm:gap-8 text-sm">
          {/* Streak */}
          <div className="flex items-center gap-1.5" data-testid="stat-streak">
            <Flame className="w-4 h-4 text-orange-300" />
            <span className="font-bold">{currentStreak}</span>
            <span className="text-emerald-100 hidden sm:inline">Day Streak</span>
          </div>
          
          {/* XP */}
          <div className="flex items-center gap-1.5" data-testid="stat-xp">
            <Zap className="w-4 h-4 text-yellow-300" />
            <span className="font-bold">{totalXP}</span>
            <span className="text-emerald-100 hidden sm:inline">XP</span>
          </div>
          
          {/* Accuracy */}
          <div className="flex items-center gap-1.5" data-testid="stat-accuracy">
            <Target className="w-4 h-4 text-cyan-300" />
            <span className="font-bold">{accuracy.toFixed(0)}%</span>
            <span className="text-emerald-100 hidden sm:inline">Accuracy</span>
          </div>
          
          {/* Questions */}
          <div className="flex items-center gap-1.5" data-testid="stat-questions">
            <CheckCircle2 className="w-4 h-4 text-green-300" />
            <span className="font-bold">{totalQuestions}</span>
            <span className="text-emerald-100 hidden sm:inline">Questions</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Teacher Stats Bar - Shows Classes, Students, Broadcasts, Messages
export function TeacherStatsBar({ teacher, school }) {
  const classCount = teacher?.assignments ? Object.keys(teacher.assignments).length : 0;

  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2 px-4 shadow-md" data-testid="teacher-stats-bar">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-around sm:justify-center sm:gap-8 text-sm">
          {/* Classes */}
          <div className="flex items-center gap-1.5" data-testid="stat-classes">
            <GraduationCap className="w-4 h-4 text-blue-200" />
            <span className="font-bold">{classCount}</span>
            <span className="text-blue-100 hidden sm:inline">Classes</span>
          </div>
          
          {/* School */}
          <div className="hidden md:flex items-center gap-1.5" data-testid="stat-school">
            <BookOpen className="w-4 h-4 text-blue-200" />
            <span className="font-medium text-blue-100 truncate max-w-[150px]">{school?.name || "School"}</span>
          </div>
          
          {/* Teacher ID */}
          <div className="flex items-center gap-1.5" data-testid="stat-teacher-id">
            <Users className="w-4 h-4 text-blue-200" />
            <span className="font-mono text-xs">{teacher?.teacher_id}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Parent Stats Bar - Shows Children, Credits, Subscription Status
export function ParentStatsBar({ parent, childrenList = [], homeworkCredits, subscription }) {
  const childCount = childrenList.length;
  const credits = homeworkCredits?.balance || 0;
  const planType = subscription?.plan_type || "free";

  return (
    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2 px-4 shadow-md" data-testid="parent-stats-bar">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-around sm:justify-center sm:gap-8 text-sm">
          {/* Children */}
          <div className="flex items-center gap-1.5" data-testid="stat-children">
            <Users className="w-4 h-4 text-indigo-200" />
            <span className="font-bold">{childCount}</span>
            <span className="text-indigo-100 hidden sm:inline">{childCount === 1 ? 'Child' : 'Children'}</span>
          </div>
          
          {/* Homework Credits */}
          <div className="flex items-center gap-1.5" data-testid="stat-credits">
            <Zap className="w-4 h-4 text-yellow-300" />
            <span className="font-bold">{credits}</span>
            <span className="text-indigo-100 hidden sm:inline">Credits</span>
          </div>
          
          {/* Plan */}
          <div className="flex items-center gap-1.5" data-testid="stat-plan">
            <CreditCard className="w-4 h-4 text-purple-200" />
            <span className="font-bold capitalize">{planType}</span>
            <span className="text-indigo-100 hidden sm:inline">Plan</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// School Stats Bar - Shows Students, Teachers, Analytics
export function SchoolStatsBar({ school, admin }) {
  const studentCount = school?.student_count || 0;
  const teacherCount = school?.teacher_count || 0;
  const planName = school?.subscription_tier || "starter";

  return (
    <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-2 px-4 shadow-md" data-testid="school-stats-bar">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-around sm:justify-center sm:gap-8 text-sm">
          {/* School Code */}
          <div className="flex items-center gap-1.5" data-testid="stat-school-code">
            <BookOpen className="w-4 h-4 text-teal-200" />
            <span className="font-mono text-xs">{school?.school_code}</span>
          </div>
          
          {/* Students */}
          <div className="flex items-center gap-1.5" data-testid="stat-students">
            <Users className="w-4 h-4 text-cyan-200" />
            <span className="font-bold">{studentCount}</span>
            <span className="text-teal-100 hidden sm:inline">Students</span>
          </div>
          
          {/* Teachers */}
          <div className="flex items-center gap-1.5" data-testid="stat-teachers">
            <GraduationCap className="w-4 h-4 text-cyan-200" />
            <span className="font-bold">{teacherCount}</span>
            <span className="text-teal-100 hidden sm:inline">Teachers</span>
          </div>
          
          {/* Plan */}
          <div className="flex items-center gap-1.5" data-testid="stat-school-plan">
            <BarChart3 className="w-4 h-4 text-teal-200" />
            <span className="font-bold capitalize">{planName}</span>
            <span className="text-teal-100 hidden sm:inline">Plan</span>
          </div>
        </div>
      </div>
    </div>
  );
}
